import { useCallback, useState } from 'react';
import type { MouseEvent } from 'react';
import type { IPartnersContact } from 'app/services/partnersApi';
import { NON_CLICKABLE_OPAQUE } from '../readOnly';
import { clearMenus, useDropdownAutoClose } from 'hooks/useBootstrapDropdown';
import ContactRow from './ContactRow';
import ContactSearchRow from './ContactSearchRow';
import AddedContactRow from './AddedContactRow';

/**
 * Phase 7a/7b — `.col-xs-7.contact-content.contact_<id>`, the right half of a
 * company's tab pane (`tmpl/partners.tmpl:210-266`).
 *
 * ★★ Q8 — A CONTACT SEARCH DESTROYS THIS PANE, PERMANENTLY.
 * `contactRender` (`partners.js:280-281`) does:
 *
 * ```js
 * var search_contact = $(`.contact_${contact_delete_row}`);
 * search_contact.empty();
 * collection.forEach(row => search_contact.append(`<div class="row every-row" …>`));
 * ```
 *
 * `contact_<id>` is on **two** elements — this wrapper (`partners.tmpl:222`)
 * AND the inner `.col-xs-12.scrollable-area.contact_fields_block`
 * (`partners.tmpl:232`). jQuery empties the OUTER one first, which takes the
 * ADD button and the whole scroll container down with it; the second `.empty()`
 * then runs against a node that is already detached. The rows are appended into
 * both members of the set, so the only one anybody sees is the outer wrapper —
 * i.e. the searched rows land DIRECTLY in `.contact-content`, with:
 *   - no `#add_contact` button (so you cannot add a contact any more), and
 *   - no `.scrollable-area` (`height:calc(100% - 50px); overflow:auto`,
 *     styles.scss:364), so a long list now overflows the pane instead of
 *     scrolling inside it.
 * Clearing the search box does NOT undo it — that is another keyup, another
 * `contactRender`, the same destroyed shape with more rows in it. Recovery is
 * reload-only (or, in 7c, a full `render()` from a company add/delete).
 *
 * That is what `searchRows` models: `null` until this company's pane has been
 * contact-searched, and thereafter the surviving flat shape. MIRRORED ON
 * PURPOSE — do not restore the button or the scroll box.
 *
 * ★ `id="add_contact"` IS DUPLICATED 42 TIMES — once per company pane — and
 * that duplication is LOAD-BEARING (discovery §6.3 / Q12): the button is
 * positioned BY ID, `#add_contact { margin-right:15px; margin-top:-75px;
 * z-index:5 }` (styles.css:483-487). Render it with a class instead and the
 * ADD button drops 75px out of the Contact header row. Invalid HTML, kept
 * verbatim; no lint rule in this repo objects today (no eslint config is
 * checked in) — if one is added, suppress it here rather than "fixing" the id.
 *
 * ★ Two nested `.row` divs wrap the ADD button with no element between them —
 * a real quirk of the template, kept (the grid gutters depend on it).
 *
 * ★ NO EMPTY STATE. A company with no contacts renders the ADD button and an
 * empty `.scrollable-area`; legacy shows no "no contacts" text and neither do
 * we (discovery §1 States). Note that such a company can never reach the
 * destroyed shape either: with no contacts the search filter returns `[]` and
 * `contactRender` throws before touching the DOM (Q7).
 *
 * ★ ORDER IS SERVER ORDER. `getCompanyContactCollection` (`partners.js:174-185`)
 * walks `contactCollection.models` — i.e. the `GET /contacts/` array, already
 * ordered by `name` (`project/models.py:66`) — and pushes matches in that
 * order. `searchContactName` (`:253-258`) filters that same array in place, so
 * the searched list keeps the order too. No client-side sort anywhere.
 *
 * ★ THE CONTACT KEBAB IS CLICK-ONLY. `.dropdown_contact` is `display:none`
 * until `.every-row:hover` → `display:inline` (styles.scss:369, :392), and
 * `view/partners.js` binds NO mouseover for it — unlike the company kebab. The
 * menu itself is opened purely by Bootstrap's dropdown data-api on CLICK
 * (`bootstrap.js:895`), which `react.html` does not ship, so in wave A the
 * contact delete item was unreachable. Restored here via
 * `../useBootstrapDropdown`; the state lives at pane level so the page carries
 * one open menu, not one per row.
 *
 * ★ 7c — `click #add_contact` is delegated on `#ksdm-content` by `PartnersPage`
 * (exactly as `partners.js:29` delegates it), so this component binds no
 * handler to the button; it only has to render the RESULT of an add. Legacy's
 * result is a raw HTML string appended to `.contact-content.contact_<id>
 * .col-xs-12` (`partners.js:210`) — the single `.col-xs-12` inside the pane,
 * i.e. `.scrollable-area.contact_fields_block` — with NO re-render, and that
 * string is markup-different from the template row (Q2). `appendedIds` is what
 * models it: the created contact is pushed onto the CONTACTS COLLECTION (so
 * field edits on it can find their model, and so the next `render()` promotes it
 * to a template row) while this list renders it through `AddedContactRow`
 * instead of `ContactRow`, after every template row, in append order.
 *
 * ★ AND AFTER A CONTACT SEARCH THE APPEND TARGET IS GONE. Q8 emptied
 * `.contact-content.contact_<id>`, so `.col-xs-12` matches NOTHING inside it and
 * `content.append(...)` writes into an empty jQuery set: the POST and its echo
 * PATCH still happen, the contact really is created, and NOTHING appears on
 * screen until a `render()` or a reload. The `searchRows` branch below
 * reproduces that by ignoring `contacts`/`appendedIds` entirely. (Reaching it
 * needs Q11's tab/`activeCompanyId` divergence, since the search also destroys
 * the ADD button in the pane it renders into.)
 */
interface ContactListProps {
  companyId: number;
  contacts: IPartnersContact[];
  /**
   * `null` until `contactRender` has run for this company; afterwards the rows
   * it last appended, in a pane that has lost its ADD button and scroll area.
   */
  searchRows: IPartnersContact[] | null;
  /**
   * Contacts that entered the collection through `addContact` and have NOT been
   * through a `render()` yet — they render as `AddedContactRow` (Q2), not
   * `ContactRow`. Page-wide (contact ids are unique), cleared by `render()`.
   */
  appendedIds: ReadonlySet<number>;
  /**
   * ★ NOT A LEGACY PROP. True when the current user does not own this company
   * (`PartnersPage.editableIds`). A contact belongs to its company, so a company
   * you may only read has contacts you may only read: every row's three inputs
   * go `readonly` + `non-clickable`, the row's ⋮ kebab is not rendered at all
   * (so it cannot appear on hover — its only item is Delete), and the ADD
   * button is `disabled` + `non-clickable`.
   *
   * ★ THIS IS STRICTER THAN THE API. `ProjectCompanyContactPersonViewSet` never
   * filters by owner (`api/rest_project/viewset.py:940`, `:955`), so the contact
   * endpoints would still accept these writes — the lock is a decision about
   * what belongs to whom, not a mirror of a 404 the way the company fields are.
   */
  readOnly?: boolean;
}

const ContactList = ({
  companyId,
  contacts,
  searchRows,
  appendedIds,
  readOnly = false,
}: ContactListProps) => {
  /** Bootstrap's `.open`, one per pane (`getParent()` → the bare `<div class="">`). */
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const closeMenu = useCallback(() => setOpenMenuId(null), []);
  useDropdownAutoClose(openMenuId !== null, closeMenu);

  /** `Dropdown.prototype.toggle` — bootstrap.js:763-794. See CompanyRail. */
  const handleToggleMenu = useCallback(
    (e: MouseEvent<HTMLButtonElement>, contactId: number) => {
      const isActive = openMenuId === contactId;
      clearMenus();
      if (!isActive) setOpenMenuId(contactId);
      e.preventDefault();
      e.stopPropagation();
    },
    [openMenuId]
  );

  if (searchRows) {
    // Q8 — the post-`contactRender` shape: the wrapper keeps its own attributes
    // (`.empty()` only removes children) and the searched rows are its ONLY
    // children. No `.row`/`.btn-contact-block`, no `#add_contact`, no
    // `.scrollable-area.contact_fields_block`.
    return (
      <div className={`col-xs-7 contact-content contact_${companyId}`} data-id={companyId}>
        {searchRows.map((contact) => (
          <ContactSearchRow
            key={contact.id}
            contact={contact}
            open={openMenuId === contact.id}
            onToggleMenu={handleToggleMenu}
            readOnly={readOnly}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={`col-xs-7 contact-content contact_${companyId}`} data-id={companyId}>
      <div className="row">
        <div className="row">
          <div className="col-xs-2 col-xs-offset-10 btn-contact-block">
            {/* No `type` attribute in the template either — it defaults to
                `submit`, which is inert outside a <form>. Kept absent so the
                rendered attribute set matches. */}
            <button
              id="add_contact"
              /* `non-clickable` — the app-wide read-only affordance. */
              className={`ka-has-tooltip btn-add btn add${readOnly ? ' non-clickable' : ''}`}
              style={readOnly ? NON_CLICKABLE_OPAQUE : undefined}
              data-id={companyId}
              tooltip="Add new contact"
              /* Read-only pane: a disabled button fires no click, so the
                 `#add_contact` delegate never runs. `PartnersPage.addContact`
                 re-checks anyway. */
              disabled={readOnly}
            >
              <i style={{ fontSize: '17px', paddingRight: '6px' }}>+</i> ADD
            </button>
          </div>
        </div>
      </div>
      <div className={`col-xs-12 scrollable-area contact_fields_block contact_${companyId}`}>
        {/* The rows the last `render()` produced from the collection… */}
        {contacts
          .filter((contact) => !appendedIds.has(contact.id))
          .map((contact) => (
            <ContactRow
              key={contact.id}
              contact={contact}
              companyId={companyId}
              open={openMenuId === contact.id}
              onToggleMenu={handleToggleMenu}
              readOnly={readOnly}
            />
          ))}
        {/* …then everything `addContact` has appended SINCE, in append order
            (`PartnersPage` pushes each created contact onto the end of the
            collection). `content.append(...)` (`partners.js:212`) puts the new
            row last inside `.scrollable-area`, after the template rows, and
            leaves the existing ones untouched. */}
        {contacts
          .filter((contact) => appendedIds.has(contact.id))
          .map((contact) => (
            <AddedContactRow
              key={contact.id}
              contact={contact}
              /* `self.company_id` is the STRING from `dataset.id` (`:204`) and
                 is interpolated into `data-name` verbatim. */
              companyId={String(companyId)}
              open={openMenuId === contact.id}
              onToggleMenu={handleToggleMenu}
              readOnly={readOnly}
            />
          ))}
      </div>
    </div>
  );
};

export default ContactList;
